import { useEffect, useState } from 'react';
import { FormikProps, getIn } from 'formik';
import { RoutesContextProps, useTranslation } from '@shared/core';
import { useContactCollectorGuestTelemetryTelemetry } from '../../ContactCollectorGuest.telemetry';
import { contactCollectorGuestRoutePaths } from '../..';
import { ContactInputFields, Contacts } from '../../ContactCollectorGuest.controller';
import { Contacts as IframeContacts, ContactInputFields as IframeContactInputFields } from '@apps/contactCollector/iframe/ContactCollectorIframe.controller';
import { OptionType } from '@withjoy/joykit';
import { useFeatureValue } from '@shared/core/featureFlags';
import { CountryData } from 'react-intl-tel-input';
import { FieldVisibility } from './Form';
import { isUnitedStatesCode } from '@apps/card/utils';

export const e164Regex = /^\+{0,1}\d{0,15}$/;

export const useFormController = (
  formik: FormikProps<Contacts> | FormikProps<IframeContacts>,
  isIframeVersion: boolean,
  e164PhoneNumber: { [key: number]: string },
  setE164PhoneNumber: React.Dispatch<React.SetStateAction<{ [key: number]: string }>>,
  coupleNames?: string,
  routeHelpers?: RoutesContextProps,
  phoneNumberRule: FieldVisibility = 'optional',
  mailingAddressRule: FieldVisibility = 'optional',
  disableAddressValidation: boolean = false,
) => {
  const { value: contactCollectionInternationalAddressValidationEnabled } = useFeatureValue('contactCollectionInternationalAddressValidationEnabled');
  const { payload: contactCollectionDisablePhoneNumberValidationForFlagPayload = [] } = useFeatureValue('contactCollectionDisablePhoneNumberValidationFor');
  const { buttonInteracted } = useContactCollectorGuestTelemetryTelemetry();
  const { t } = useTranslation('contactCollector');
  const tAdmin = t('admin');
  const [isPhoneNumberValid, setIsPhoneNumberValid] = useState<{ [key: number]: boolean }>({ 0: false });
  const [phoneNumberFocused, setPhoneNumberFocused] = useState<{ [key: number]: boolean }>({ 0: false });

  const isPhoneNumberInvalid = (index: number) => {
    // If phone number is hidden, it's never invalid
    if (phoneNumberRule === 'hidden') return false;
    // If phone number is required and empty, it's invalid
    if (phoneNumberRule === 'required' && phoneNumberFocused[index] && !formik.values.contacts[index].phoneNumber) return true;
    // Otherwise check the validation
    return !!formik.values.contacts[index].phoneNumber && phoneNumberFocused[index] && !isPhoneNumberValid[index];
  };

  const isEmailInvalid = (index: number) => index === 0 && !formik.values.contacts[index].email && !!formik.touched.contacts?.[index].email;

  useEffect(() => {
    if (!coupleNames && !isIframeVersion && !!routeHelpers) {
      routeHelpers.goToExactRoute(contactCollectorGuestRoutePaths.landing.path);
    }
  }, [coupleNames, isIframeVersion, routeHelpers]);

  const getFormFieldError = (fieldName: keyof ContactInputFields | IframeContactInputFields, index: number) => {
    const { errors, touched } = formik;
    const name = `contacts[${index}][${fieldName}]`;
    const error = getIn(errors, name);
    const touch = getIn(touched, name);

    return touch && error ? error : undefined;
  };

  const handleOnCountryDropdownChange = (option: OptionType | null, index: number): void => {
    if (!option) return;

    formik.setFieldValue(`contacts[${index}].country`, option.label);
    formik.setFieldValue(`contacts[${index}].countryCode`, option.value);
  };

  const handleOnPhoneNumberChange = (isValid: boolean, value: string, selectedCountryData: CountryData, fullNumber: string, extension: string, index: number): void => {
    const sanitizedValue = value.replace(/[^0-9+]/g, '');
    if (!e164Regex.test(sanitizedValue)) {
      return;
    }
    setIsPhoneNumberValid(prevState => ({
      ...prevState,
      [index]: isValid || (contactCollectionDisablePhoneNumberValidationForFlagPayload as string[])?.includes(selectedCountryData?.iso2 ?? ''),
    }));
    formik.setFieldValue(`contacts[${index}].phoneNumber`, sanitizedValue);
    // remove empty spaces and - to convert to e164 format
    setE164PhoneNumber(prevState => ({ ...prevState, [index]: fullNumber.replace(/ |-/g, '').trim() }));
  };

  const handleOnPhoneNumberFocus = (isValid: boolean, value: string, selectedCountryData: CountryData, fullNumber: string, extension: string, index: number): void => {
    if (!phoneNumberFocused[index]) {
      setPhoneNumberFocused(prevState => ({ ...prevState, [index]: true }));
    }
    handleOnPhoneNumberChange(isValid, value, selectedCountryData, fullNumber, extension, index);
  };

  const handleOnSaveDetailsClick = () => {
    Object.entries(e164PhoneNumber).forEach(element => element.length === 2 && formik.setFieldValue(`contacts[${element[0]}].phoneNumber`, element[1]));
    buttonInteracted({ label: 'SaveDetails' });
    const noAddress = !formik.values.contacts[0].address1;
    const isMailingAddressHidden = mailingAddressRule === 'hidden';
    const isNationalAddress = isUnitedStatesCode(formik.values.contacts[0].countryCode);
    const skipAddressValidation = isMailingAddressHidden || (!isNationalAddress && !contactCollectionInternationalAddressValidationEnabled);
    if ((isIframeVersion && (noAddress || skipAddressValidation)) || disableAddressValidation) {
      return formik.handleSubmit();
    }
    if (!!routeHelpers) {
      routeHelpers.goToExactRoute(skipAddressValidation ? contactCollectorGuestRoutePaths.confirmation.path : contactCollectorGuestRoutePaths.address.path);
    }
    // else { consider putting a telemetry event here for address validation skipped }
  };

  return {
    tAdmin,
    handleOnSaveDetailsClick,
    isFormValid: formik.isValid,
    errors: {
      firstNameError: (index: number) => getFormFieldError('firstName', index),
      lastNameError: (index: number) => getFormFieldError('lastName', index),
      emailError: (index: number) => getFormFieldError('email', index),
      address1Error: (index: number) => getFormFieldError('address1', index),
      postalCodeError: (index: number) => getFormFieldError('postalCode', index),
      cityError: (index: number) => getFormFieldError('city', index),
      regionError: (index: number) => getFormFieldError('region', index),
      countryError: (index: number) => getFormFieldError('country', index),
    },
    handleOnCountryDropdownChange,
    handleOnPhoneNumberChange,
    handleOnPhoneNumberFocus,
    isPhoneNumberInvalid,
    isEmailInvalid,
  };
};
